import React, { useEffect, useState } from 'react';
import { ImageFrame, isMediaPresent } from '../../../media';
import { proposalPxToSpacingCss } from '../../../utils/proposalUnits';
import { DEFAULT_BORDER_COLOR } from '../../../../shared/boxes/borderStyles';
import ProposalBoxAlign from '../ProposalBoxAlign/ProposalBoxAlign';
import styles from './ProposalImage.module.css';
import ImageEnlargement from '../../../media/ImageEnlargement';
import { useImageFocalPoint } from '../../../media/useImageFocalPoint';

function alignmentClassKey(alignment) {
  const a = String(alignment || 'center').toLowerCase();
  if (a === 'start' || a === 'left') return 'left';
  if (a === 'end' || a === 'right') return 'right';
  if (a === 'center' || a === 'middle') return 'center';
  return 'center';
}

/** Rendu drawMode (positionnement absolu) — px, sans ProposalBoxAlign. */
function ProposalImageDrawMode({
  id,
  src,
  srcSet,
  alt,
  alignment,
  maxWidth,
  width,
  height,
  backgroundColor,
  borderColor,
  borderWidth,
  opacity,
  mediaFit,
  mediaAspectRatio,
  mediaPosition,
  mediaFocalPoint,
  isEditing,
  allowEnlarge,
  broken,
  onBroken,
}) {
  const focal = useImageFocalPoint(src, mediaFocalPoint, mediaFit);
  const alignKey = alignmentClassKey(alignment);
  const alignClass = styles[alignKey] || styles.center;

  if (!isMediaPresent(src) || broken) {
    // Viewer : ni cadre vide ni placeholder — le bloc disparaît.
    if (!isEditing) return null;
    return (
      <div id={id} className={`${styles.wrapper} ${alignClass}`}>
        <div className={styles.broken}>Image non disponible</div>
      </div>
    );
  }

  const wrapperStyle = {
    width: '100%',
    boxSizing: 'border-box',
    ...(maxWidth != null && maxWidth !== ''
      ? { maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth }
      : {}),
    ...(width != null && width !== ''
      ? { maxWidth: typeof width === 'number' ? `${width}px` : width }
      : {}),
    ...(backgroundColor ? { backgroundColor } : {}),
    ...(borderWidth > 0 ? { border: `${borderWidth}px solid ${borderColor || DEFAULT_BORDER_COLOR}` } : {}),
    ...(opacity != null ? { opacity } : {}),
  };

  const fixedHeight = height != null && height !== '';
  const fixedRatio = width && fixedHeight
    ? Number(width) / Number(height)
    : null;

  if (fixedHeight && !mediaAspectRatio) {
    const imgStyle = {
      display: 'block',
      maxWidth: '100%',
      width: width != null ? (typeof width === 'number' ? `${width}px` : width) : '100%',
      height: typeof height === 'number' ? `${height}px` : height,
      objectFit: mediaFit === 'contain' ? 'contain' : 'cover',
      ...((focal.objectPosition ?? mediaPosition) ? { objectPosition: focal.objectPosition ?? mediaPosition } : {}),
    };
    return (
      <div id={id} className={`${styles.wrapper} ${alignClass}`} style={wrapperStyle}>
        <ImageEnlargement enabled={allowEnlarge && !isEditing && !broken} src={src} srcSet={srcSet} alt={alt}>
          <img
            ref={focal.ref}
            src={src}
            srcSet={srcSet || undefined}
            alt={alt}
            loading="lazy"
            className={styles.image}
            style={imgStyle}
            onError={onBroken}
          />
        </ImageEnlargement>
      </div>
    );
  }

  return (
    <div id={id} className={`${styles.wrapper} ${alignClass}`} style={wrapperStyle}>
      <ImageEnlargement enabled={allowEnlarge && !isEditing && !broken} src={src} srcSet={srcSet} alt={alt}>
        <ImageFrame
          src={src}
          srcSet={srcSet}
          alt={alt}
          mediaFit={mediaFit}
          mediaAspectRatio={mediaAspectRatio}
          mediaPosition={mediaPosition}
          mediaFocalPoint={mediaFocalPoint}
          fallbackRatio={fixedRatio ?? undefined}
          isEditing={isEditing}
          onError={onBroken}
        />
      </ImageEnlargement>
    </div>
  );
}

/**
 * Bloc Puck — Image issue du proposal.
 */
const ProposalImage = ({
  id,
  src = '',
  srcSet = '',
  alt = '',
  drawMode = false,
  /** @deprecated utiliser alignHorizontal */
  alignment = 'center',
  alignHorizontal,
  alignVertical = 'center',
  maxWidth,
  width,
  height,
  backgroundColor,
  borderColor,
  borderWidth = 0,
  /** Opacité de l'image du proposal (0 à 1), fond et bordure compris. */
  opacity,
  mediaFit = 'cover',
  mediaAspectRatio = 'auto',
  /** object-position CSS, décalage de l'image en mode remplir. */
  mediaPosition,
  mediaFocalPoint,
  isEditing = false,
  allowEnlarge = false,
}) => {
  const [broken, setBroken] = useState(false);

  // Image corrigée (src change) → nouvel essai de chargement.
  useEffect(() => {
    setBroken(false);
  }, [src]);

  // Viewer : une image en erreur est masquée (pas d'espace vide ni d'icône
  // cassée). En édition le placeholder reste visible pour corriger.
  if (broken && !isEditing) return null;

  if (drawMode) {
    return (
      <ProposalImageDrawMode
        id={id}
        src={src}
        srcSet={srcSet}
        alt={alt}
        alignment={alignment}
        maxWidth={maxWidth}
        width={width}
        height={height}
        backgroundColor={backgroundColor}
        borderColor={borderColor}
        borderWidth={borderWidth}
        opacity={opacity}
        mediaFit={mediaFit}
        mediaAspectRatio={mediaAspectRatio}
        mediaPosition={mediaPosition}
        mediaFocalPoint={mediaFocalPoint}
        isEditing={isEditing}
        allowEnlarge={allowEnlarge}
        broken={broken}
        onBroken={() => setBroken(true)}
      />
    );
  }

  const hAlign = alignHorizontal ?? alignment ?? 'center';
  const borderCss = borderWidth > 0
    ? `${proposalPxToSpacingCss(borderWidth) || `${borderWidth}px`} solid ${borderColor || DEFAULT_BORDER_COLOR}`
    : undefined;

  const wrapperStyle = {
    boxSizing: 'border-box',
    ...(backgroundColor ? { backgroundColor } : {}),
    ...(borderCss ? { border: borderCss } : {}),
    ...(opacity != null ? { opacity } : {}),
  };

  if (!isMediaPresent(src) || broken) {
    // Viewer : ni cadre vide ni placeholder — le bloc disparaît.
    if (!isEditing) return null;
    return (
      <ProposalBoxAlign id={id} horizontal={hAlign} vertical={alignVertical} innerMode="shrink">
        <div className={styles.broken} style={wrapperStyle}>Image non disponible</div>
      </ProposalBoxAlign>
    );
  }

  // Largeur du champ rightbar (px) — prime sur maxWidth legacy du parser.
  const sizeStyle = width != null && width !== ''
    ? { width: typeof width === 'number' ? `${width}px` : width, maxWidth: '100%' }
    : (maxWidth != null && maxWidth !== ''
      ? { maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth }
      : undefined);

  /* Toute la hauteur : l'image couvre sa colonne, quelle que soit la hauteur
     que lui donne la ligne (image face à un panneau de texte). */
  if (alignVertical === 'stretch') {
    return (
      <ProposalBoxAlign id={id} horizontal={hAlign} vertical="stretch" style={wrapperStyle}>
        <div className={styles.stretchFrame} style={sizeStyle}>
          <ImageEnlargement fill enabled={allowEnlarge && !isEditing && !broken} src={src} srcSet={srcSet} alt={alt}>
            <ImageFrame
              src={src}
              srcSet={srcSet}
              alt={alt}
              mediaFit={mediaFit}
              mediaPosition={mediaPosition}
              mediaFocalPoint={mediaFocalPoint}
              isEditing={isEditing}
              asBackground
              onError={() => setBroken(true)}
            />
          </ImageEnlargement>
        </div>
      </ProposalBoxAlign>
    );
  }

  return (
    <ProposalBoxAlign id={id} horizontal={hAlign} vertical={alignVertical} innerMode="shrink" style={wrapperStyle}>
      <ImageEnlargement style={sizeStyle} enabled={allowEnlarge && !isEditing && !broken} src={src} srcSet={srcSet} alt={alt}>
        <ImageFrame
          src={src}
          srcSet={srcSet}
          alt={alt}
          mediaFit={mediaFit}
          mediaAspectRatio={mediaAspectRatio}
          mediaPosition={mediaPosition}
          mediaFocalPoint={mediaFocalPoint}
          isEditing={isEditing}
          style={allowEnlarge && !isEditing ? undefined : sizeStyle}
          onError={() => setBroken(true)}
        />
      </ImageEnlargement>
    </ProposalBoxAlign>
  );
};

export default ProposalImage;
